import { m } from '@hub/messages';
import type { AnyRoute } from '@hub/router';
import { definePageRoute, defineRedirectRoute } from '@hub/router/definePageRoute';
import { type DomainParents, defineDomainRoutes } from '@hub/router/domain-routes';
import { getCommunicationPreferencesOptions } from '@platform/api';
import { NotificationPaths } from './route.paths';
import { loadUnsubscribe } from './unsubscribe.loader';

/**
 * Factory for the authenticated notification-preferences overview, exposed so
 * the account domain can mount it under the `/account` Konto layout (the
 * accountLayoutRoute lives in account/route.manifest.ts and can't be
 * referenced from this manifest's own factory).
 */
export function createNotificationsOverviewRoute(parents: DomainParents, parent: AnyRoute): AnyRoute {
  return definePageRoute(parents, {
    parent,
    path: NotificationPaths.overview,
    title: m.notifications_preferences_page_title,
    description: m.notifications_preferences_page_description,
    backBehavior: 'legacy',
    page: () => import('./Notifications.page'),
    pendingComponent: () => import('./components/NotificationPreferenceSkeleton'),
    query: getCommunicationPreferencesOptions,
    quickAction: {
      to: NotificationPaths.overview.path,
      icon: 'mail',
      title: m.notifications_quick_action_title,
      subline: m.notifications_quick_action_subline,
      surfaces: ['home-privacy'],
      order: 10,
    },
    flag: 'page-notification',
  });
}

export default function createNotificationsRoutes(parents: DomainParents) {
  const notificationUnsubscribeRoute = definePageRoute(parents, {
    path: NotificationPaths.unsubscribe,
    title: m.notifications_unsubscribe_page_title,
    page: () => import('./NotificationUnsubscribe.page'),
    pendingComponent: () => import('./components/NotificationUnsubscribeSkeleton'),
    loaderDeps: ({ search }) => ({ token: search.token, campaign: search.campaign }),
    loader: ({ deps: { token, campaign } }) => loadUnsubscribe(token, campaign),
    preload: false,
  });

  const legacyNotificationUnsubscribeRedirect = defineRedirectRoute(parents, {
    from: NotificationPaths.legacyUnsubscribe,
    to: NotificationPaths.unsubscribe,
  });

  return defineDomainRoutes({
    authenticated: [] as const,
    public: [notificationUnsubscribeRoute, legacyNotificationUnsubscribeRedirect] as const,
  });
}
